import React from 'react';
import {Box, CircularProgress, Divider, IconButton, Modal, Stack, Tooltip, Typography,} from '@mui/material';
import {alpha} from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LeftPanelTabs from '../../../../products/components/common/LeftPanelTabs';
import useSourcesFileViewerStore from '../../../../../stores/useSourcesFileViewerStore';
import SourcesFileViewerContent from './SourcesFileViewerContent';
import {SourcesSectionPortalOutlet} from './SourcesSectionReversePortal';
import useFileHighlights from './useFileHighlights';
import useResizablePanelWidth from '../../../../../hooks/useResizablePanelWidth';
import {useSession} from '../../../../../firebase/UserProvider';
import {useShallow} from 'zustand/react/shallow';
import {useDocumentSearch} from '../../../../../hooks/useDocumentSearch';
import DocumentSearchBar from './DocumentSearchBar';

// Portal destination — the full layout area (header + content) that Shell shrinks when
// CommunicationHub opens. Using report-layout-area lets the modal cover the header too,
// so the header does not bleed through above the modal.
function getReportContentContainer() {
    return document.getElementById('report-layout-area') || document.body;
}

const LEFT_PANEL_DEFAULT_WIDTH = 520;
const LEFT_PANEL_MIN_WIDTH = 300;
const LEFT_PANEL_MAX_WIDTH = 760;
/** Keep a strip wide enough for the toggle when the sources panel is collapsed. */
export const LEFT_PANEL_COLLAPSED_STRIP_WIDTH = 28;
export const LEFT_PANEL_TOGGLE_SIZE = 22;

/**
 * Collapsed handle must sit fully inside the modal card (`overflow: hidden` +
 * `borderRadius`). A negative `right` on a 0-width rail clips the chevron.
 */
export function leftPanelTogglePosition(collapsed) {
    return collapsed
        ? {
            left: (LEFT_PANEL_COLLAPSED_STRIP_WIDTH - LEFT_PANEL_TOGGLE_SIZE) / 2,
            right: 'auto',
        }
        : {
            left: 'auto',
            right: -LEFT_PANEL_TOGGLE_SIZE / 2,
        };
}

const RIGHT_TABS = Object.freeze([
    {value: 'highlights', label: 'Highlights', accent: 'warning'},
]);

function VerticalRailTab({label, accent, isActive, onClick, ariaLabel}) {
    return (
        <Stack
            component="button"
            onClick={onClick}
            aria-label={ariaLabel}
            aria-pressed={isActive}
            sx={(theme) => {
                const accentMain = theme.palette[accent].main;
                const accentContrast = theme.palette[accent].contrastText;
                return {
                    all: 'unset',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    width: '100%',
                    px: 0.75,
                    py: 1.5,
                    my: 0.75,
                    borderRadius: 1,
                    typography: 'caption',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    letterSpacing: '0.04em',
                    color: isActive ? accentContrast : accentMain,
                    bgcolor: isActive ? accentMain : alpha(accentMain, 0.16),
                    border: `1px solid ${alpha(accentMain, isActive ? 1 : 0.45)}`,
                    '&:hover': {
                        bgcolor: isActive ? accentMain : alpha(accentMain, 0.3),
                    },
                    transition: 'color 0.15s, background-color 0.15s, border-color 0.15s',
                    userSelect: 'none',
                };
            }}
        >
            {label}
        </Stack>
    );
}

function HighlightCard({highlight, onDelete, isActive, onClick}) {
    const firstRegion = highlight?.regions?.[0];
    // Prefer the exact selected text for a TEXT_RANGE highlight; fall back to the segment preview.
    const selectedText = firstRegion?.regionData?.text || null;
    const preview = selectedText || firstRegion?.preview || null;

    return (
        <Stack
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.(e);
                }
            }}
            sx={(theme) => ({
                cursor: 'pointer',
                boxSizing: 'border-box',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                p: 1.25,
                pr: 4,
                borderRadius: 1.5,
                border: `1px solid ${isActive ? theme.palette.warning.main : theme.palette.divider}`,
                bgcolor: isActive ? alpha(theme.palette.warning.main, 0.08) : 'background.paper',
                '&:hover': {
                    bgcolor: alpha(theme.palette.warning.main, 0.06),
                    borderColor: theme.palette.warning.light,
                },
                '&:focus-visible': {
                    outline: `2px solid ${theme.palette.warning.main}`,
                    outlineOffset: 2,
                },
                transition: 'background-color 0.15s, border-color 0.15s',
                textAlign: 'left',
                width: '100%',
            })}
        >
            <Tooltip title="Delete highlight" placement="top" arrow>
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onDelete(highlight.id); }}
                    aria-label="Delete highlight"
                    sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        color: 'text.disabled',
                        '&:hover': {color: 'error.main'},
                    }}
                >
                    <DeleteOutlineIcon sx={{fontSize: 14}}/>
                </IconButton>
            </Tooltip>
            {preview && (
                <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                        fontSize: '0.8rem',
                        lineHeight: 1.45,
                        wordBreak: 'break-word',
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        borderLeft: (theme) => `2px solid ${theme.palette.warning.light}`,
                        pl: 0.75,
                    }}
                >
                    {preview}
                </Typography>
            )}
        </Stack>
    );
}

// Hoisted static sx objects (rendering-hoist-jsx)
const BACKDROP_SX = {position: 'absolute', inset: 0, bgcolor: 'rgba(0, 0, 0, 0.38)', zIndex: 4};
const VIEWER_SX = {position: 'absolute', inset: {xs: 1.5, md: 3}, outline: 'none', zIndex: 5};

/**
 * Shared source-file viewer modal.
 *
 * Default (no props): store-driven via {@link useSourcesFileViewerStore} — the original
 * FINTEL report-editor / CT behavior, unchanged.
 *
 * Additive props (P9, used by the Collected Material viewer host — contract C2):
 * @param {Object}   [controlled]  When provided the modal ignores the store and renders from
 *                                 {isOpen, fileId, canonicalFileId, fileName, fileType,
 *                                 fileSource, sentenceHit, onClose}.
 * @param {ReactNode} [headerSlot]  Rendered above the file content (gray CM metadata header).
 * @param {boolean}  [headerExpand] When true with a contentSlot, header + content share one
 *                                  scroll region (IIR viewer). Without contentSlot, the header
 *                                  grows with its content instead of capping at 45% height.
 * @param {ReactNode} [contentSlot] Replaces the file content entirely (e.g. "no content"
 *                                  notice; P10 can plug IIR rendering here).
 * @param {Function} [onBack]      Renders a circular "<" back button in the top-left corner.
 * @param {boolean}  [hideContentToolbar]  Hides the file renderer title/download bar (CM header owns those actions).
 */
const SourcesFileViewerModal = ({
    controlled = null,
    headerSlot = null,
    headerExpand = false,
    contentSlot = null,
    onBack = null,
    hideContentToolbar = false,
}) => {
    // rerender-defer-reads: batch all file-descriptor reads into one selector.
    // useShallow does a shallow equality check on the returned object so Zustand
    // doesn't trigger a re-render when the same primitive values come back.
    const storeState = useSourcesFileViewerStore(useShallow((s) => ({
        isOpen: s.isOpen,
        close: s.close,
        reportId: s.reportId,
        fileId: s.fileId,
        canonicalFileId: s.canonicalFileId,
        fileName: s.fileName,
        fileType: s.fileType,
        fileSource: s.fileSource,
        sentenceHit: s.sentenceHit,
    })));

    const isControlled = controlled !== null;
    const noopClose = React.useCallback(() => {}, []);
    const isOpen = isControlled ? Boolean(controlled.isOpen) : storeState.isOpen;
    const close = isControlled ? (controlled.onClose || noopClose) : storeState.close;
    const fileId = isControlled ? (controlled.fileId ?? null) : storeState.fileId;
    const reportId = isControlled ? (controlled.reportId ?? null) : storeState.reportId;
    const canonicalFileId = isControlled ? (controlled.canonicalFileId ?? null) : storeState.canonicalFileId;
    const fileName = isControlled ? (controlled.fileName ?? null) : storeState.fileName;
    const fileType = isControlled ? (controlled.fileType ?? 'unknown') : storeState.fileType;
    const fileSource = isControlled ? (controlled.fileSource ?? null) : storeState.fileSource;
    const sentenceHit = isControlled ? (controlled.sentenceHit ?? null) : storeState.sentenceHit;

    const {user} = useSession() || {};
    const userId = user?.uid || null;
    const {highlights, isLoading: isLoadingHighlights, deleteHighlight, createHighlight} = useFileHighlights(fileId, null, null, userId);
    const [activeRightTab, setActiveRightTab] = React.useState(null);
    const rightPanelOpen = activeRightTab !== null;
    const [leftCollapsed, setLeftCollapsed] = React.useState(false);
    const [activeHighlightId, setActiveHighlightId] = React.useState(null);

    // ── In-document search ────────────────────────────────────────────────────
    // The PM content is fetched inside SourcesFileRenderer; it reports it back
    // to us via onPmContentReady so we can run useDocumentSearch here at the
    // modal level and thread the results back down as props.
    const [pmContentForSearch, setPmContentForSearch] = React.useState(null);
    const [searchOpen, setSearchOpen] = React.useState(false);
    const [hasDualPane, setHasDualPane] = React.useState(false);

    const {query, setQuery, matches, activeIdx, navigate, clear} = useDocumentSearch(pmContentForSearch);

    // Reset search state whenever the viewed file changes.
    React.useEffect(() => {
        setSearchOpen(false);
        clear();
        setPmContentForSearch(null);
        setHasDualPane(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId]);

    // Reset also when the modal closes.
    React.useEffect(() => {
        if (!isOpen) {
            setSearchOpen(false);
            clear();
            setPmContentForSearch(null);
            setHasDualPane(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleOpenSearch = React.useCallback(() => setSearchOpen(true), []);
    const handleCloseSearch = React.useCallback(() => {
        setSearchOpen(false);
        clear();
    }, [clear]);
    // ─────────────────────────────────────────────────────────────────────────

    const handleRailTabClick = React.useCallback((tabValue) => {
        setActiveRightTab((prev) => (prev === tabValue ? null : tabValue));
    }, []);

    // Auto-open the Highlights panel once per opened document when highlights exist, and again the
    // first time the user creates one. Tracked per fileId so reopening a different doc re-evaluates,
    // and so a user who manually collapses the panel isn't fought on every render.
    const autoOpenedForFileRef = React.useRef(null);
    React.useEffect(() => {
        // Reset the per-file guard whenever the viewer closes or switches files.
        if (!isOpen) {
            autoOpenedForFileRef.current = null;
            setActiveRightTab(null);
            return;
        }
        if (autoOpenedForFileRef.current !== fileId) {
            // New file in this open session: collapse until we know whether it has highlights.
            autoOpenedForFileRef.current = fileId ? `pending:${fileId}` : null;
            setActiveRightTab(null);
        }
    }, [isOpen, fileId]);

    React.useEffect(() => {
        if (!isOpen || isLoadingHighlights) return;
        // Open the panel right away when a freshly-opened document already has highlights.
        if (autoOpenedForFileRef.current === `pending:${fileId}` && highlights.length > 0) {
            autoOpenedForFileRef.current = fileId;
            setActiveRightTab('highlights');
        }
    }, [isOpen, fileId, isLoadingHighlights, highlights.length]);

    // Wrap create so the panel pops open after the user makes their first highlight.
    const handleCreateHighlight = React.useCallback(async (args) => {
        const created = await createHighlight(args);
        if (created) {
            setActiveRightTab('highlights');
            // Activate the freshly created highlight so its <mark> renders on the source text
            // immediately. Without this the highlight is saved and listed in the panel but the
            // selected span is not visually highlighted until the user clicks the panel entry.
            if (created.id != null) setActiveHighlightId(created.id);
        }
        return created;
    }, [createHighlight]);

    const {width: leftPanelWidth, isResizing: isLeftResizing, onResizeStart: onLeftResizeStart} = useResizablePanelWidth({
        storageKey: 'sourcesFileViewer.leftPanelWidth',
        defaultWidth: LEFT_PANEL_DEFAULT_WIDTH,
        min: LEFT_PANEL_MIN_WIDTH,
        max: LEFT_PANEL_MAX_WIDTH,
    });
    const isSourcesTabActive = isOpen;

    const leftTabs = React.useMemo(() => ([
        {
            value: 'sources',
            label: 'Sources',
            content: (
                <SourcesSectionPortalOutlet
                    hostId="sources-file-viewer-left-panel"
                    isActive={isSourcesTabActive}
                />
            ),
        },
    ]), [isSourcesTabActive]);

    // Derive the highlighted PM block indices for the active highlight card. Highlights store a
    // DB segmentId, but the renderer marks blocks by their 0-based PM block index. Prefer the block
    // index we stored in regionData (authoritative for image-derived docs where one segment can't
    // represent every PM block); fall back to the segment's numberInItem for older highlights.
    const blockIndexOf = (r) => (Number.isInteger(r?.regionData?.blockIndex)
        ? r.regionData.blockIndex
        : r?.numberInItem);

    const activeHighlightSegmentIds = React.useMemo(() => {
        if (!activeHighlightId) return null;
        const h = highlights.find((x) => x.id === activeHighlightId);
        if (!h || !Array.isArray(h.regions)) return null;
        return h.regions
            .map(blockIndexOf)
            .filter((n) => Number.isInteger(n));
    }, [activeHighlightId, highlights]);

    // For TEXT_RANGE regions, carry the char offsets keyed by block index so the renderer marks
    // only the selected sentence(s) instead of the whole block. FULL_SEGMENT regions are omitted
    // (no entry → whole-block highlight).
    const activeHighlightRanges = React.useMemo(() => {
        if (!activeHighlightId) return null;
        const h = highlights.find((x) => x.id === activeHighlightId);
        if (!h || !Array.isArray(h.regions)) return null;
        const map = {};
        h.regions.forEach((r) => {
            const blockIndex = blockIndexOf(r);
            if (!Number.isInteger(blockIndex)) return;
            const rd = r.regionData;
            const start = rd?.startOffset;
            const end = rd?.endOffset;
            if (r.regionType === 'TEXT_RANGE' && Number.isInteger(start) && Number.isInteger(end) && end > start) {
                map[blockIndex] = {startOffset: start, endOffset: end, text: rd?.text || null};
            }
        });
        return Object.keys(map).length ? map : null;
    }, [activeHighlightId, highlights]);

    return (
        <Modal
            open={isOpen}
            onClose={close}
            aria-label="Source file viewer"
            container={getReportContentContainer}
            sx={{position: 'absolute'}}
            slotProps={{backdrop: {sx: BACKDROP_SX}}}
        >
            {/*
             * position:absolute here is relative to #report-content-area (position:relative).
             * That container lives inside Shell's App which already applies marginRight when
             * CommunicationHub opens — so the modal panel shrinks automatically with no
             * extra state needed.
             */}
            <Stack
                tabIndex={-1}
                onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
                        e.preventDefault();
                        handleOpenSearch();
                    }
                }}
                sx={{...VIEWER_SX, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}
                p={2}
            >
                <Stack
                    sx={(theme) => ({
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: theme.shadows[12],
                    })}
                >
                    {onBack && (
                        <Tooltip title="Back to previous view" placement="right" arrow>
                            <IconButton
                                onClick={onBack}
                                aria-label="Back to previous view"
                                sx={(theme) => ({
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    zIndex: 20,
                                    width: 32,
                                    height: 32,
                                    bgcolor: 'background.paper',
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: '50%',
                                    boxShadow: theme.shadows[1],
                                    '&:hover': {bgcolor: 'action.hover'},
                                })}
                            >
                                <ChevronLeftIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                    <Stack direction="row" flex={1} minHeight={0}>
                        <Box
                            data-testid="file-viewer-left-rail"
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                flexShrink: 0,
                                width: leftCollapsed ? LEFT_PANEL_COLLAPSED_STRIP_WIDTH : 'auto',
                            }}
                        >
                            <Stack sx={{
                                width: leftCollapsed ? 0 : leftPanelWidth,
                                flexShrink: 0,
                                overflow: 'hidden',
                                transition: isLeftResizing ? 'none' : 'width 0.2s ease',
                                borderRight: leftCollapsed ? 0 : 1,
                                borderColor: 'divider',
                            }}>
                                <LeftPanelTabs
                                    tabs={leftTabs}
                                    activeTab="sources"
                                    onChange={() => {}}
                                    ariaLabel="File viewer left panel tabs"
                                />
                            </Stack>

                            {/* Drag-to-resize handle on the panel's right edge (hidden while collapsed) */}
                            {!leftCollapsed && (
                                <Box
                                    role="separator"
                                    aria-orientation="vertical"
                                    aria-label="Resize file viewer left panel"
                                    onPointerDown={onLeftResizeStart}
                                    sx={(theme) => ({
                                        position: 'absolute',
                                        top: 0,
                                        bottom: 0,
                                        right: -3,
                                        width: 6,
                                        zIndex: 11,
                                        cursor: 'col-resize',
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.25),
                                        },
                                        ...(isLeftResizing && {
                                            bgcolor: alpha(theme.palette.primary.main, 0.4),
                                        }),
                                    })}
                                />
                            )}

                            {/* Collapse / expand toggle pinned to the divider edge */}
                            <Tooltip title={leftCollapsed ? 'Expand panel' : 'Collapse panel'} placement="right" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => setLeftCollapsed((c) => !c)}
                                    aria-label={leftCollapsed ? 'Expand panel' : 'Collapse panel'}
                                    sx={(theme) => ({
                                        position: 'absolute',
                                        top: 52,
                                        zIndex: 10,
                                        width: LEFT_PANEL_TOGGLE_SIZE,
                                        height: LEFT_PANEL_TOGGLE_SIZE,
                                        bgcolor: 'background.paper',
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: '50%',
                                        boxShadow: theme.shadows[1],
                                        ...leftPanelTogglePosition(leftCollapsed),
                                        '&:hover': {bgcolor: 'action.hover'},
                                        '& .MuiSvgIcon-root': {fontSize: '0.9rem'},
                                    })}
                                >
                                    {leftCollapsed ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Stack sx={{flex: 1, minWidth: 0, minHeight: 0, position: 'relative'}}>
                            {headerExpand && headerSlot && contentSlot ? (
                                // IIR viewer: metadata + EEI + tags + body scroll as one unit.
                                <Stack sx={{flex: 1, minHeight: 0, overflowY: 'auto'}}>
                                    <Box sx={{flexShrink: 0, borderBottom: 1, borderColor: 'divider'}}>
                                        {headerSlot}
                                    </Box>
                                    <Box sx={{px: 2, py: 1}}>
                                        {contentSlot}
                                    </Box>
                                </Stack>
                            ) : (
                                <>
                                    {headerSlot && (
                                        <Box sx={{
                                            flexShrink: 0,
                                            ...(headerExpand ? {} : {
                                                maxHeight: '45%',
                                                overflowY: 'auto',
                                            }),
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                        }}>
                                            {headerSlot}
                                        </Box>
                                    )}
                                    {(() => {
                                        if (contentSlot) {
                                            return (
                                                <Stack sx={{flex: 1, minHeight: 0, overflowY: 'auto'}} p={2}>
                                                    {contentSlot}
                                                </Stack>
                                            );
                                        }
                                        const content = (
                                            <SourcesFileViewerContent
                                                reportId={reportId}
                                                fileId={fileId}
                                                canonicalFileId={canonicalFileId}
                                                fileName={fileName}
                                                fileType={fileType}
                                                fileSource={fileSource}
                                                sentenceHit={sentenceHit}
                                                active={isOpen}
                                                savedHighlights={highlights}
                                                activeHighlightSegmentIds={activeHighlightSegmentIds}
                                                activeHighlightRanges={activeHighlightRanges}
                                                onCreateHighlight={handleCreateHighlight}
                                                searchMatches={searchOpen ? matches : null}
                                                activeSearchMatchIdx={searchOpen ? activeIdx : -1}
                                                onPmContentReady={setPmContentForSearch}
                                                onDualPaneChange={setHasDualPane}
                                                hideToolbar={hideContentToolbar}
                                            />
                                        );
                                        // The renderer sizes itself with flex:1/minHeight:0; it always
                                        // needs a bounded flex column to resolve against.
                                        return (
                                            <Box sx={{
                                                flex: 1,
                                                minHeight: 0,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                overflow: 'hidden',
                                            }}
                                            >
                                                {content}
                                            </Box>
                                        );
                                    })()}
                                </>
                            )}
                            {searchOpen && (
                                <DocumentSearchBar
                                    query={query}
                                    setQuery={setQuery}
                                    matchCount={matches.length}
                                    activeIdx={activeIdx}
                                    onNavigate={navigate}
                                    onClose={handleCloseSearch}
                                    hasDualPane={hasDualPane}
                                />
                            )}
                        </Stack>

                        <Divider orientation="vertical"/>

                        {/* Collapsed: vertical tab strip — expanded: full panel */}
                        <Stack
                            direction="row"
                            sx={{
                                flexShrink: 0,
                                borderLeft: rightPanelOpen ? 1 : 0,
                                borderColor: 'divider',
                            }}
                        >
                            {/* Vertical tab rail — always visible */}
                            <Stack
                                sx={(theme) => ({
                                    flexShrink: 0,
                                    width: 40,
                                    borderRight: rightPanelOpen ? `1px solid ${theme.palette.divider}` : 'none',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    px: 0.5,
                                    gap: 0,
                                })}
                            >
                                {/* Close button — fixed height matches the file-name header row */}
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={(theme) => ({
                                        width: '100%',
                                        height: 48,
                                        flexShrink: 0,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    })}
                                >
                                    <Tooltip title="Close" placement="left" arrow>
                                        <IconButton onClick={close} size="small" aria-label="Close file viewer overlay">
                                            <CloseRoundedIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                </Stack>

                                {RIGHT_TABS.map(({value, label, accent}) => (
                                    <Tooltip
                                        key={value}
                                        title={activeRightTab === value ? 'Collapse panel' : label}
                                        placement="left"
                                        arrow
                                    >
                                        <VerticalRailTab
                                            label={label}
                                            accent={accent}
                                            ariaLabel={label}
                                            isActive={activeRightTab === value}
                                            onClick={() => handleRailTabClick(value)}
                                        />
                                    </Tooltip>
                                ))}
                            </Stack>

                            {/* Expanded panel content */}
                            <Stack
                                sx={{
                                    width: rightPanelOpen ? 288 : 0,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    transition: 'width 0.2s ease',
                                    minHeight: 0,
                                }}
                            >
                                {rightPanelOpen && (
                                    <React.Fragment>
                                        {/* Panel header with count badge */}
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{borderBottom: 1, borderColor: 'divider', minHeight: 48, flexShrink: 0, px: 2}}
                                        >
                                            <Typography variant="subtitle2" fontWeight={700} color="warning.main">
                                                Highlights
                                                {highlights.length > 0 && (
                                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ml: 0.75}}>
                                                        ({highlights.length})
                                                    </Typography>
                                                )}
                                            </Typography>
                                        </Stack>

                                        {/* Highlights list */}
                                        <Stack sx={{flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5}} spacing={1}>
                                            {isLoadingHighlights && (
                                                <Stack alignItems="center" justifyContent="center" py={3}>
                                                    <CircularProgress size={20}/>
                                                </Stack>
                                            )}
                                            {!isLoadingHighlights && highlights.length === 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{px: 0.5, py: 1}}>
                                                    No highlights yet. Select text in the document to create one.
                                                </Typography>
                                            )}
                                            {!isLoadingHighlights && highlights.map((h) => (
                                                <HighlightCard
                                                    key={h.id}
                                                    highlight={h}
                                                    isActive={activeHighlightId === h.id}
                                                    onClick={() => setActiveHighlightId((prev) => prev === h.id ? null : h.id)}
                                                    onDelete={deleteHighlight}
                                                />
                                            ))}
                                        </Stack>
                                    </React.Fragment>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Modal>
    );
};

export default SourcesFileViewerModal;


